import { useDarkMode } from "../../../context/ThemeContext";
import { getThemeClasses } from "../../../utils/ThemeClasses";
import { useForm, Controller } from "react-hook-form";
import { useReportsStore } from "../../../store/reports/reportsStore";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAlert } from "../../../context/AlertContext";
import InfiniteDeviceSelect, { useReportDeviceIds } from "../../../components/InfiniteDeviceSelect";
import { allDevicesQueryOptions } from "../../../features/devices/queries";
import { useReportCompanyScope } from "../hooks/useReportCompanyScope";
import { useReportSpeedStore } from "../../../store/reports/reportSpeedStore";
import ReportPointsTable, { type ReportTableColumn } from "../components/ReportPointsTable";
import ReportRouteMap, { type MapRoute, type SelectedRoutePoint } from "../components/ReportRouteMap";
import { ReportStatCard, ReportChart, tickColor, gridColor, reportLegend } from "../components/ReportCharts";
import ReportLoadingPanel from "../components/ReportLoadingPanel";
import { toLocalInputValue, makeDateTimeFormatter, resolveTz } from "../utils/reportDateFormat";
import { exportHistoricToExcel, exportHistoricToPdf } from "../utils/exportHistoric";
import type { RoutePoint, RouteStatus } from "../../../store/admin/vehicleRouteTrackingStore";
import type { ChartConfiguration } from "chart.js";
import ReportActionButton from "../components/ReportActionButton";
import ReportDateRangePicker from "../components/ReportDateRangePicker";
import ReportFormatToggle from "../components/ReportFormatToggle";

const MAX_RANGE_DAYS = 31;
const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

interface SpeedFormData {
  devices: string[];
  startDate: string;
  endDate: string;
  typeReport: string;
  typeDocument: "excel" | "pdf";
  minSpeed: string;
}

interface SpeedRow {
  id: string;
  unidad: string;
  fecha: string;
  velocidad: string;
  duracion: string;
  distancia: string;
  coordenadas: string;
  lat: number;
  lng: number;
  ts: number;
  status: RouteStatus;
}

const SPEED_COLUMNS: ReportTableColumn<SpeedRow>[] = [
  { key: "unidad", header: "Unidad" },
  { key: "fecha", header: "Fecha" },
  { key: "velocidad", header: "Velocidad", align: "right" },
  { key: "duracion", header: "Duración" },
  { key: "distancia", header: "Distancia", align: "right" },
  { key: "coordenadas", header: "Coordenadas" },
];

const fmtDuration = (seconds: number): string => {
  if (!seconds || seconds < 0) return "00:00:00";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

export default function SpeedSection() {
  const { isDarkMode } = useDarkMode();
  const themeClasses = getThemeClasses(isDarkMode);
  const generateHistoryReport = useReportsStore((state) => state.generateHistoryReport);
  const loading = useReportsStore((state) => state.loading);
  const { loadedDeviceIds, loadedCount, totalDevices } = useReportDeviceIds();

  const loadRange = useReportSpeedStore((state) => state.loadRange);
  const clearSpeed = useReportSpeedStore((state) => state.clear);
  const resultsState = useReportSpeedStore((state) => state.results);
  const order = useReportSpeedStore((state) => state.order);
  const speedLoading = useReportSpeedStore((state) => state.loading);
  const tz = useReportSpeedStore((state) => state.tz);

  const [exporting, setExporting] = useState<"pdf" | "excel" | null>(null);
  const [loadAttempted, setLoadAttempted] = useState(false);
  const [selectedPoint, setSelectedPoint] = useState<SelectedRoutePoint | null>(null);
  const clearSelectedPoint = useCallback(() => setSelectedPoint(null), []);

  const onRowClick = useCallback((row: SpeedRow) => {
    setSelectedPoint((prev) =>
      prev?.vehicleName === row.unidad && prev?.ts === row.ts
        ? null
        : {
            vehicleName: row.unidad,
            ts: row.ts,
            lat: row.lat,
            lng: row.lng,
            fecha: row.fecha,
            velocidad: row.velocidad,
            status: row.status,
          },
    );
  }, []);

  const isRowSelected = useCallback(
    (row: SpeedRow) =>
      selectedPoint != null &&
      selectedPoint.vehicleName === row.unidad &&
      selectedPoint.ts === row.ts,
    [selectedPoint],
  );

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    getValues,
    formState: { errors },
  } = useForm<SpeedFormData>({
    mode: "onBlur",
    defaultValues: {
      devices: [],
      startDate: "",
      endDate: "",
      typeReport: "speed",
      typeDocument: "pdf",
      minSpeed: "",
    },
  });

  const { showAlert } = useAlert();
  const { companyId, disableWithoutCompany } = useReportCompanyScope(setValue, () => {
    clearSpeed();
    setLoadAttempted(false);
    setSelectedPoint(null);
  });
  const { data: allDevices } = useQuery({ ...allDevicesQueryOptions(companyId) });

  const selectedDevices = watch("devices");
  const startDate = watch("startDate");
  const endDate = watch("endDate");

  const hasDevices = (selectedDevices?.length ?? 0) > 0;
  const hasDates = Boolean(startDate && endDate);
  const rangeDays = useMemo(() => {
    if (!hasDates) return 0;
    return (new Date(endDate).getTime() - new Date(startDate).getTime()) / DAY_MS;
  }, [endDate, hasDates, startDate]);
  const hasValidRange = hasDates && rangeDays >= 0;
  const isWithinViewLimit = hasValidRange && rangeDays <= MAX_RANGE_DAYS;
  const filtersReadyForView = hasDevices && hasValidRange && isWithinViewLimit;

  const handleSelectAll = () => {
    if (selectedDevices?.length === loadedDeviceIds.length) {
      setValue("devices", []);
    } else {
      setValue("devices", loadedDeviceIds);
    }
  };

  const onSubmit = async (formData: SpeedFormData) => {
    const result = await generateHistoryReport(
      {
        deviceIds: formData.devices,
        startDate: formData.startDate,
        endDate: formData.endDate,
        typeReport: formData.typeReport,
        typeDocument: formData.typeDocument,
        minSpeed: formData.minSpeed,
      },
      showAlert,
      () => {},
    );

    if (result.success && result.data) {
      const extension = formData.typeDocument === "pdf" ? "pdf" : "xlsx";
      const blob = new Blob([result.data], {
        type:
          formData.typeDocument === "pdf"
            ? "application/pdf"
            : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.fileName || `reporte.${extension}`;
      link.click();
      window.URL.revokeObjectURL(url);
    }
  };

  const resolveNameById = useCallback((): Record<string, string> => {
    const map: Record<string, string> = {};
    (allDevices ?? []).forEach((d: any) => {
      map[d.id] = d.vehicle?.name || d.identifier || d.id;
    });
    return map;
  }, [allDevices]);

  const onViewSpeed = async () => {
    const { devices, startDate, endDate, minSpeed } = getValues();

    if (!devices || devices.length === 0) {
      showAlert({
        title: "Selecciona unidades",
        text: "Selecciona al menos un equipo para ver los excesos.",
        icon: "warning",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        showCancelButton: false,
      });
      return;
    }

    if (!startDate || !endDate) {
      showAlert({
        title: "Fechas requeridas",
        text: "Selecciona la fecha de inicio y fin.",
        icon: "warning",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        showCancelButton: false,
      });
      return;
    }

    const days = (new Date(endDate).getTime() - new Date(startDate).getTime()) / DAY_MS;
    if (days > MAX_RANGE_DAYS) {
      showAlert({
        title: "Rango demasiado amplio",
        text: `Ver excesos solo muestra hasta ${MAX_RANGE_DAYS} días. Para rangos mayores usa "Generar Reporte".`,
        icon: "warning",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        showCancelButton: false,
      });
      return;
    }

    const fromIso = new Date(startDate).toISOString();
    const toIso = new Date(endDate).toISOString();
    const nameById = resolveNameById();
    const devicesToLoad = devices.map((id) => ({
      deviceId: id,
      vehicleName: nameById[id] || id,
    }));

    setSelectedPoint(null);
    await loadRange(devicesToLoad, fromIso, toIso, { minSpeed: parseFloat(minSpeed) || 0 });
    setLoadAttempted(true);
  };

  const orderedResults = useMemo(
    () => order.map((id) => resultsState[id]).filter(Boolean),
    [order, resultsState],
  );

  const totals = useMemo(() => {
    let totalExcesos = 0;
    let totalDistance = 0;
    let speedSum = 0;
    let maxSpeed = 0;
    orderedResults.forEach((r) => {
      totalExcesos += r.totalExcesos;
      totalDistance += r.totalDistance;
      speedSum += r.speedSum;
      if (r.maxSpeed > maxSpeed) maxSpeed = r.maxSpeed;
    });
    const avgExceso = totalExcesos > 0 ? speedSum / totalExcesos : 0;
    return { totalExcesos, totalDistance, avgExceso, maxSpeed };
  }, [orderedResults]);

  const fmtDateTime = useMemo(() => makeDateTimeFormatter(resolveTz(tz), false), [tz]);

  const tableRows = useMemo<SpeedRow[]>(
    () =>
      orderedResults.flatMap((r) =>
        r.excesos.map((e, i) => ({
          id: `${r.deviceId}-${e.ts}-${i}`,
          unidad: r.vehicleName,
          fecha: fmtDateTime(e.ts),
          velocidad: `${e.speed.toFixed(0)} km/h`,
          duracion: fmtDuration(e.durationSeconds),
          distancia: `${e.distanceKm.toFixed(2)} km`,
          coordenadas: `${e.lat.toFixed(5)}, ${e.lon.toFixed(5)}`,
          lat: e.lat,
          lng: e.lon,
          ts: e.ts,
          status: "driving" as RouteStatus,
        })),
      ),
    [orderedResults, fmtDateTime],
  );

  const mapRoutes = useMemo<MapRoute[]>(
    () =>
      orderedResults
        .map((r) => ({
          deviceId: r.deviceId,
          vehicleName: r.vehicleName,
          color: r.color,
          points: r.excesos.map(
            (e) =>
              ({
                lat: e.lat,
                lng: e.lon,
                ts: e.ts,
                speed: e.speed,
                heading: null,
                ignition: null,
                battery: null,
                altitude: null,
                eventCode: null,
                engineStatus: null,
                gpsSat: null,
                status: "driving",
                isPanic: false,
              }) as RoutePoint,
          ),
        }))
        .filter((r) => r.points.length > 0),
    [orderedResults],
  );

  const chartConfig = useMemo<ChartConfiguration | null>(() => {
    const withSeries = orderedResults.filter((r) => r.series.length > 0);
    if (withSeries.length === 0) return null;

    let minTs = Infinity;
    let maxTs = -Infinity;
    withSeries.forEach((r) =>
      r.series.forEach((p) => {
        if (p.ts < minTs) minTs = p.ts;
        if (p.ts > maxTs) maxTs = p.ts;
      }),
    );

    const span = maxTs - minTs;
    const bucketMs = span > 3 * DAY_MS ? DAY_MS : HOUR_MS;
    const bucketLabel = bucketMs === DAY_MS ? "día" : "hora";

    const axisFormatter = new Intl.DateTimeFormat("es-ES", {
      timeZone: resolveTz(tz),
      day: "2-digit",
      month: "2-digit",
      ...(bucketMs === HOUR_MS ? { hour: "2-digit", minute: "2-digit", hour12: false } : {}),
    });

    const datasets: any[] = withSeries.map((r) => {
      const byBucket = new Map<number, number>();
      r.series.forEach((p) => {
        const bucket = Math.floor(p.ts / bucketMs) * bucketMs;
        const prev = byBucket.get(bucket);
        if (prev === undefined || p.speed > prev) byBucket.set(bucket, p.speed);
      });
      const data = Array.from(byBucket.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([x, y]) => ({ x, y: Number(y.toFixed(0)) }));
      return {
        label: r.vehicleName,
        data,
        borderColor: r.color,
        backgroundColor: r.color,
        borderWidth: 2.5,
        pointRadius: 2,
        pointHoverRadius: 4,
        tension: 0.3,
      };
    });

    const minSpeedVal = parseFloat(getValues("minSpeed")) || 0;
    if (minSpeedVal > 0) {
      datasets.push({
        label: `Límite (${minSpeedVal} km/h)`,
        data: [
          { x: minTs, y: minSpeedVal },
          { x: maxTs, y: minSpeedVal },
        ],
        borderColor: "#DC2626",
        borderWidth: 1.75,
        borderDash: [6, 4],
        pointRadius: 0,
      });
    }

    return {
      type: "line",
      data: { datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "nearest", intersect: false },
        plugins: {
          legend: reportLegend(isDarkMode),
          title: {
            display: true,
            text: `Velocidad máxima por ${bucketLabel}`,
            color: tickColor(isDarkMode),
          },
          tooltip: {
            callbacks: {
              title: (items: any[]) => fmtDateTime(Number(items[0].parsed.x)),
              label: (item: any) => `${item.dataset.label}: ${item.parsed.y} km/h`,
            },
          },
        },
        scales: {
          x: {
            type: "linear",
            ticks: {
              color: tickColor(isDarkMode),
              callback: (value) => axisFormatter.format(new Date(Number(value))),
              maxTicksLimit: 10,
              maxRotation: 0,
              autoSkip: true,
            },
            grid: { color: gridColor(isDarkMode) },
          },
          y: {
            title: { display: true, text: "km/h", color: tickColor(isDarkMode) },
            beginAtZero: true,
            ticks: { color: tickColor(isDarkMode) },
            grid: { color: gridColor(isDarkMode) },
          },
        },
      },
    };
  }, [orderedResults, isDarkMode, tz, getValues, fmtDateTime]);

  const onDownload = async (typeDocument: "pdf" | "excel") => {
    if (tableRows.length === 0) return;
    const { startDate, endDate } = getValues();
    const extension = typeDocument === "pdf" ? "pdf" : "xlsx";
    const fileName = `speed_${startDate.split("T")[0]}_${endDate.split("T")[0]}.${extension}`;
    const period =
      `Período: ${startDate.replace("T", " ")} al ${endDate.replace("T", " ")}` +
      ` | Excesos: ${totals.totalExcesos} | Vel. máx: ${totals.maxSpeed.toFixed(0)} km/h`;

    setExporting(typeDocument);
    await new Promise((r) => setTimeout(r, 50));
    try {
      const meta = { title: "Reporte de Velocidad Máxima", period, fileName };
      if (typeDocument === "excel") {
        await exportHistoricToExcel(tableRows, SPEED_COLUMNS, meta);
      } else {
        exportHistoricToPdf(tableRows, SPEED_COLUMNS, meta);
      }
    } catch {
      showAlert({
        title: "Error al descargar",
        text: "No se pudo generar el archivo de excesos.",
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        showCancelButton: false,
      });
    } finally {
      setExporting(null);
    }
  };

  useEffect(() => {
    setValue("startDate", toLocalInputValue(new Date()));
    setValue("endDate", toLocalInputValue(new Date(new Date().setDate(new Date().getDate() + 15))));
  }, []);

  useEffect(() => {
    return () => {
      clearSpeed();
      setLoadAttempted(false);
    };
  }, [clearSpeed]);

  const hasResults = orderedResults.length > 0;
  const showSkeleton = speedLoading;
  const showEmptyState = !hasResults && !showSkeleton;

  const summaryCards = [
    { label: "Total de excesos", value: String(totals.totalExcesos), icon: "ri-alarm-warning-line" },
    { label: "Velocidad máxima", value: `${totals.maxSpeed.toFixed(0)} km/h`, icon: "ri-speed-up-line" },
    { label: "Promedio de exceso", value: `${totals.avgExceso.toFixed(0)} km/h`, icon: "ri-dashboard-3-line" },
    { label: "Distancia en exceso", value: `${totals.totalDistance.toFixed(2)} km`, icon: "ri-route-line" },
  ];

  return (
    <div className="h-full w-full overflow-y-auto p-2 sm:p-4">
      <form onSubmit={handleSubmit(onSubmit)} className={`flex flex-col lg:flex-row ${themeClasses.cardBorder} rounded-lg ${themeClasses.cardBg} p-3 sm:p-3.5 gap-3 justify-between lg:items-end`}>
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4 w-full lg:w-auto">
          <div className="flex flex-col gap-1 min-w-52 w-full lg:w-auto">
            <div className="flex justify-between items-center">
              <label htmlFor="devices" className={`${themeClasses.text} text-xs`}>
                Equipos {selectedDevices?.length > 0 && `(${selectedDevices.length}/${totalDevices})`}
              </label>
              <button
                type="button"
                onClick={handleSelectAll}
                disabled={disableWithoutCompany}
                className={`text-xs px-2 py-1 rounded ${themeClasses.buttonBg} text-white ${themeClasses.buttonHover2} hover:opacity-90 disabled:opacity-50`}
              >
                {selectedDevices?.length === loadedDeviceIds.length && loadedCount > 0 ? "Deseleccionar todos" : `Seleccionar todos (${loadedCount})`}
              </button>
            </div>
            <div>
              <Controller
                name="devices"
                control={control}
                rules={{ required: "Selecciona al menos un equipo" }}
                render={({ field }) => (
                  <InfiniteDeviceSelect
                    value={field.value}
                    onChange={field.onChange}
                    isDarkMode={isDarkMode}
                    themeClasses={themeClasses}
                  />
                )}
              />
              {errors.devices && <span className="text-red-500 text-sm">{errors.devices.message}</span>}
            </div>
          </div>
          <div className="flex flex-col gap-1 w-full lg:w-auto">
            <label htmlFor="minSpeed" className={`${themeClasses.text} text-xs`}>Limite de velocidad</label>
            <div className="flex flex-col gap-1">
              <input
                {...register("minSpeed")}
                className={`h-10 px-2 rounded-lg ${themeClasses.inputBg} ${themeClasses.inputBorder} ${themeClasses.inputText} ${themeClasses.inputPlaceholder} border-2 w-full lg:w-32`} placeholder="80"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1 w-full lg:w-auto">
            <label className={`${themeClasses.text} text-xs`}>Rango de fechas</label>
            <input type="hidden" {...register("startDate", { required: "Fecha inicio requerida" })} />
            <input type="hidden" {...register("endDate", { required: "Fecha fin requerida" })} />
            <ReportDateRangePicker
              startValue={watch("startDate")}
              endValue={watch("endDate")}
              onChangeStart={(v) => setValue("startDate", v, { shouldValidate: true, shouldDirty: true })}
              onChangeEnd={(v) => setValue("endDate", v, { shouldValidate: true, shouldDirty: true })}
              startError={errors.startDate?.message}
              endError={errors.endDate?.message}
            />
          </div>
          <div className="flex flex-col gap-1 w-full lg:w-auto">
            <label className={`${themeClasses.text} text-xs`}>Formato</label>
            <Controller
              name="typeDocument"
              control={control}
              render={({ field }) => <ReportFormatToggle value={field.value} onChange={field.onChange} />}
            />
          </div>
        </div>
        <div className="flex items-center gap-2 w-full lg:w-auto">
          <ReportActionButton
            type="button"
            variant="secondary"
            onClick={onViewSpeed}
            disabled={speedLoading || !filtersReadyForView || disableWithoutCompany}
            title={!filtersReadyForView && hasDevices && hasValidRange && !isWithinViewLimit ? `Ver excesos solo admite hasta ${MAX_RANGE_DAYS} días` : undefined}
          >
            Ver excesos
          </ReportActionButton>
          <ReportActionButton
            type="submit"
            disabled={loading || !hasDevices || !hasValidRange || disableWithoutCompany}
          >
            {loading ? "Generando..." : "Generar Reporte"}
          </ReportActionButton>
        </div>
      </form>

      {showSkeleton && (
        <ReportLoadingPanel
          isDarkMode={isDarkMode}
          icon="ri-speed-up-line"
          title="Calculando excesos de velocidad…"
          subtitle="Obteniendo posiciones del rango seleccionado"
        />
      )}

      {hasResults && !showSkeleton && (
        <div className={`mt-4 flex flex-col gap-4 ${themeClasses.cardBorder} rounded-lg ${themeClasses.cardBg} p-3 sm:p-4`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className={`text-sm ${themeClasses.text}`}>
              {orderedResults.length} unidad(es) · {totals.totalExcesos} exceso(s)
            </span>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => onDownload("pdf")}
                disabled={exporting !== null || tableRows.length === 0}
                className={`text-xs px-3 py-1 rounded ${themeClasses.buttonBg} text-white ${themeClasses.buttonHover2} disabled:opacity-50`}
              >
                {exporting === "pdf" ? "Generando..." : "Descargar PDF"}
              </button>
              <button
                type="button"
                onClick={() => onDownload("excel")}
                disabled={exporting !== null || tableRows.length === 0}
                className={`text-xs px-3 py-1 rounded ${themeClasses.buttonBg} text-white ${themeClasses.buttonHover2} disabled:opacity-50`}
              >
                {exporting === "excel" ? "Generando..." : "Descargar Excel"}
              </button>
              <button
                type="button"
                onClick={() => {
                  clearSpeed();
                  setLoadAttempted(false);
                  setSelectedPoint(null);
                }}
                className={`text-xs px-3 py-1 rounded ${themeClasses.buttonBg} text-white ${themeClasses.buttonHover2}`}
              >
                Cerrar
              </button>
            </div>
          </div>

          <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
            {summaryCards.map((card) => (
              <ReportStatCard key={card.label} icon={card.icon} label={card.label} value={card.value} isDarkMode={isDarkMode} />
            ))}
          </div>

          {chartConfig && (
            <div className={`p-3 rounded-xl border ${isDarkMode ? "bg-dark-card border-dark-border" : "bg-gray-50 border-gray-200"}`}>
              <ReportChart config={chartConfig} height={300} />
            </div>
          )}

          {mapRoutes.length > 0 && (
            <div className={`sticky top-0 z-20 -mx-3 sm:-mx-4 px-3 sm:px-4 py-2 ${isDarkMode ? "bg-dark-card" : "bg-gray-50"} border-b ${themeClasses.cardBorder} shadow-sm`}>
              <p className={`text-xs mb-2 ${themeClasses.textSecondary}`}>
                Pasa el cursor sobre la ruta para ver la hora de cada punto, o haz clic para fijar el detalle.
              </p>
              <ReportRouteMap
                routes={mapRoutes}
                tz={tz || undefined}
                selected={selectedPoint}
                onSelect={setSelectedPoint}
                onClearSelected={clearSelectedPoint}
              />
            </div>
          )}

          <ReportPointsTable
            data={tableRows}
            columns={SPEED_COLUMNS}
            isDarkMode={isDarkMode}
            emptyText="No se encontraron excesos en el rango seleccionado."
            onRowClick={onRowClick}
            isRowSelected={isRowSelected}
          />
        </div>
      )}

      {showEmptyState && (
        <div className={`mt-4 flex flex-col gap-4 ${themeClasses.cardBorder} rounded-lg ${themeClasses.cardBg} p-3 sm:p-4`}>
          <div className={`relative w-full overflow-hidden rounded-xl border border-dashed ${themeClasses.cardBorder} ${isDarkMode ? "bg-white/5" : "bg-black/[0.02]"}`} style={{ minHeight: 220 }}>
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
              <span className={`inline-flex items-center justify-center w-14 h-14 rounded-full ${isDarkMode ? "bg-primary-500/15" : "bg-primary-500/10"}`}>
                <i className="ri-speed-up-line text-primary-500 text-2xl" />
              </span>
              <div>
                <p className={`text-sm font-semibold ${themeClasses.text}`}>Excesos de velocidad</p>
                <p className={`text-xs mt-1 max-w-md ${themeClasses.textSecondary}`}>
                  Configura el límite, equipos y fechas, y usa <strong>Ver excesos</strong> (hasta {MAX_RANGE_DAYS} días) para ver el resumen, el gráfico y el mapa. Para rangos mayores usa <strong>Generar Reporte</strong>.
                </p>
              </div>
            </div>
          </div>

          {loadAttempted && !speedLoading && (
            <div className={`p-3 rounded-xl border ${isDarkMode ? "bg-dark-card border-dark-border" : "bg-gray-100 border-gray-200"}`}>
              <div className="flex items-start space-x-2">
                <i className={`ri-information-line text-base ${themeClasses.textSecondary}`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-medium ${themeClasses.textSecondary}`}>Sin resultados</p>
                  <p className={`text-sm ${themeClasses.text} mt-0.5`}>
                    No se encontraron excesos en el rango seleccionado. Verifica el límite, equipos y fechas, o usa <strong>Generar Reporte</strong>.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
